import { type QueryKey, skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { isObject } from 'lodash';
import { useCallback } from 'react';

import { Canvases } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { type CanvasDetail } from '~/swr-hooks/canvases/types';
import { shouldRetryAirApiClientError } from '~/utils/shouldRetryAirApiClientError';

export const CANVAS_DETAIL = 'CANVAS_DETAIL';

export type CanvasDetailKey = [typeof CANVAS_DETAIL, UseCanvasParams];

export const getCanvasDetailKey = (params: UseCanvasParams) => {
  return [CANVAS_DETAIL, params];
};

export const isCanvasDetailKey = (key: QueryKey): key is CanvasDetailKey =>
  Array.isArray(key) && key[0] === CANVAS_DETAIL && isObject(key[1]);

export type UseCanvasParams = Omit<NonNullable<Parameters<typeof Canvases.get>[0]>, 'canvasId' | 'workspaceId'> & {
  canvasId: string | undefined;
  workspaceId: string | undefined;
};

export type UseCanvasOptions = {
  enabled?: boolean;
};

export const useCanvas = (params: UseCanvasParams, options: UseCanvasOptions = {}) => {
  const workspaceId = params.workspaceId;
  const canvasId = params.canvasId;

  const query = useQuery({
    queryKey: getCanvasDetailKey(params),
    queryFn:
      workspaceId && canvasId
        ? () =>
            callAirApi<CanvasDetail>(() =>
              Canvases.get({
                ...params,
                workspaceId,
                canvasId,
                includeInactiveNodes: true,
              }),
            )
        : skipToken,
    retry: shouldRetryAirApiClientError,
    enabled: !!workspaceId && !!canvasId && (options.enabled ?? true),
  });

  return { ...query, canvas: query.data };
};

/**
 * Returns an imperative callback that fetches (and caches) a canvas's detail
 * outside of React render — e.g. to read a canvas's existing nodes before adding
 * more to it. Returns the cached value when present, otherwise issues the request.
 *
 * Uses the `QueryClient` from `useQueryClient()` so reads/writes hit the same
 * cache the React tree is rendering against.
 */
export const useFetchCanvasDetail = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (params: UseCanvasParams & { canvasId: string; workspaceId: string }) =>
      queryClient.ensureQueryData({
        queryKey: getCanvasDetailKey(params),
        queryFn: () => callAirApi<CanvasDetail>(() => Canvases.get({ ...params, includeInactiveNodes: true })),
        retry: shouldRetryAirApiClientError,
      }),
    [queryClient],
  );
};
